import { useState, useEffect, useRef, useCallback } from 'react';

const STEPS = [
  {
    target: '[data-onboarding="council-tabs"]',
    title: 'Your councils at a glance',
    body: 'Filter by council to see new DAs, approvals, and your local development pipeline. Tap any council to focus on that area.',
    position: 'below',
  },
  {
    target: '[data-onboarding="first-da-card"]',
    title: "Explore local DAs",
    body: 'Each card shows the DA number, address, status, and estimated cost. Tap to expand for full details and a direct link to the council page.',
    position: 'above',
  },
  {
    target: '[data-onboarding="track-button"]',
    title: 'Track what matters',
    body: "Tap the bookmark icon to track a DA. You'll see all your tracked DAs in one place and get notified when the status changes.",
    position: 'left',
  },
  {
    target: '[data-onboarding="map-tab"]',
    title: 'See it on the map',
    body: "See local DAs on the map. Use 'Near Me' to find development activity around you.",
    position: 'below',
  },
  {
    target: '[data-onboarding="settings-button"]',
    title: 'Set up your 7am ritual',
    body: 'Install SiteScope on your Home Screen, then open Settings to enable device push. Weekly email is available on every plan; instant DA alerts remain Pro-only.',
    position: 'below',
  },
];

const PAD = 8;
const TOOLTIP_W = 280;

function getRect(target) {
  const el = document.querySelector(target);
  return el ? el.getBoundingClientRect() : null;
}

export default function OnboardingOverlay({ onComplete }) {
  const [step, setStep]         = useState(() => {
    // Find first step with a valid target
    for (let i = 0; i < STEPS.length; i++) {
      if (getRect(STEPS[i].target)) return i;
    }
    return 0;
  });
  const [targetRect, setTargetRect] = useState(() => getRect(STEPS[0].target));
  const [dims, setDims]         = useState({ w: window.innerWidth, h: window.innerHeight });
  const [tooltipH, setTooltipH] = useState(170);
  const [fade, setFade]         = useState(true);
  const tooltipRef              = useRef(null);

  // Measure tooltip height after each step change
  useEffect(() => {
    if (tooltipRef.current) setTooltipH(tooltipRef.current.offsetHeight);
  }, [step]);

  // Remeasure target rect on step change or resize
  const remeasure = useCallback(() => {
    const rect = getRect(STEPS[step]?.target);
    setTargetRect(rect);
    setDims({ w: window.innerWidth, h: window.innerHeight });
  }, [step]);

  useEffect(() => {
    remeasure();
    window.addEventListener('resize', remeasure);
    return () => window.removeEventListener('resize', remeasure);
  }, [remeasure]);

  // Targets can mount after this overlay's initial render. Remeasure first;
  // only skip a step when the target is still absent after commit.
  useEffect(() => {
    if (targetRect) return;
    const current = getRect(STEPS[step]?.target);
    if (current) {
      setTargetRect(current);
      return;
    }

    let next = step + 1;
    while (next < STEPS.length && !getRect(STEPS[next].target)) next++;
    if (next < STEPS.length) setStep(next);
    else onComplete();
  }, [targetRect, step, onComplete]);

  // Escape key → close
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onComplete(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onComplete]);

  const advance = () => {
    setFade(false);
    setTimeout(() => {
      let next = step + 1;
      // Skip steps whose target element doesn't exist
      while (next < STEPS.length && !getRect(STEPS[next].target)) next++;
      if (next >= STEPS.length) {
        onComplete();
        return;
      }
      setStep(next);
      setFade(true);
    }, 200);
  };

  if (!targetRect) return null;

  // ── Spotlight geometry ────────────────────────────────────────────────────
  const spotX = Math.round(targetRect.left - PAD);
  const spotY = Math.round(targetRect.top  - PAD);
  const spotW = Math.round(targetRect.width  + PAD * 2);
  const spotH = Math.round(targetRect.height + PAD * 2);

  // ── Tooltip position ──────────────────────────────────────────────────────
  const vw      = dims.w;
  const vh      = dims.h;
  const maxW    = Math.min(TOOLTIP_W, vw - 32);
  const centerX = targetRect.left + targetRect.width / 2;
  const pos     = STEPS[step].position;

  const clampLeft = x => Math.max(16, Math.min(x, vw - maxW - 16));

  let tipTop, tipLeft;

  if (pos === 'below') {
    tipTop  = targetRect.bottom + PAD + 10;
    tipLeft = clampLeft(centerX - maxW / 2);
    // Flip above if overflows bottom
    if (tipTop + tooltipH > vh - 16) {
      tipTop = targetRect.top - PAD - 10 - tooltipH;
    }
  } else if (pos === 'above') {
    tipTop  = targetRect.top - PAD - 10 - tooltipH;
    tipLeft = clampLeft(centerX - maxW / 2);
    // Flip below if overflows top
    if (tipTop < 60) {
      tipTop = targetRect.bottom + PAD + 10;
    }
  } else if (pos === 'left') {
    const leftCandidate = targetRect.left - maxW - 16;
    if (leftCandidate >= 16) {
      tipLeft = leftCandidate;
      tipTop  = Math.max(16, Math.min(
        targetRect.top + targetRect.height / 2 - tooltipH / 2,
        vh - tooltipH - 16,
      ));
    } else {
      // Not enough left-side space — go above
      tipTop  = targetRect.top - PAD - 10 - tooltipH;
      tipLeft = clampLeft(centerX - maxW / 2);
      if (tipTop < 60) tipTop = targetRect.bottom + PAD + 10;
    }
  }

  const isLast = step === STEPS.length - 1 ||
    !STEPS.slice(step + 1).some(s => getRect(s.target));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9995 }}>

      {/* ── SVG backdrop with spotlight cutout ─────────────────────────── */}
      <svg
        width={dims.w}
        height={dims.h}
        style={{ position: 'fixed', top: 0, left: 0, pointerEvents: 'none' }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="onboarding-spotlight">
            <rect x={0} y={0} width={dims.w} height={dims.h} fill="white" />
            <rect x={spotX} y={spotY} width={spotW} height={spotH} rx={8} fill="black" />
          </mask>
        </defs>
        <rect
          x={0} y={0}
          width={dims.w} height={dims.h}
          fill="rgba(0,0,0,0.65)"
          mask="url(#onboarding-spotlight)"
        />
      </svg>

      {/* ── Clickable backdrop to dismiss ──────────────────────────────── */}
      <button
        type="button"
        aria-label="Close onboarding guide"
        style={{ position: 'fixed', inset: 0, zIndex: 9996, padding: 0, border: 'none', background: 'transparent', cursor: 'default' }}
        onClick={onComplete}
      />

      {/* ── Tooltip card ───────────────────────────────────────────────── */}
      <div
        ref={tooltipRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: tipTop,
          left: tipLeft,
          width: maxW,
          zIndex: 9997,
          background: '#111827',
          border: '1px solid #d4a84a',
          borderRadius: 12,
          padding: '16px',
          fontFamily: "'DM Sans','Helvetica Neue',sans-serif",
          boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
          opacity: fade ? 1 : 0,
          transition: 'opacity 0.2s ease',
          pointerEvents: 'auto',
        }}
      >
        {/* Title */}
        <div id="onboarding-title" style={{ fontSize: 14, fontWeight: 700, color: '#e8e0d8', marginBottom: 8, lineHeight: 1.3 }}>
          {STEPS[step].title}
        </div>

        {/* Body */}
        <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6, marginBottom: 14 }}>
          {STEPS[step].body}
        </div>

        {/* Step dots */}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 14 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: 6, height: 6, borderRadius: '50%',
              background: i === step ? '#d4a84a' : '#4b5563',
              transition: 'background 0.2s',
              flexShrink: 0,
            }} />
          ))}
        </div>

        {/* Next / Get Started button */}
        <button
          onClick={advance}
          style={{
            width: '100%',
            padding: '10px',
            background: '#d4a84a',
            color: '#0a0f1a',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginBottom: 8,
          }}
        >
          {isLast ? 'Get Started' : 'Next'}
        </button>

        {/* Skip link */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={onComplete}
            style={{
              background: 'none',
              border: 'none',
              color: '#6b7280',
              fontSize: 11,
              cursor: 'pointer',
              fontFamily: 'inherit',
              padding: '2px 0',
            }}
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
